import React, { useCallback, useContext, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useFormikContext } from "formik";
import { BiRefresh } from "react-icons/bi";
import {
  Action,
  ActionChat,
  ButtonPrimary,
  ButtonPrimaryTransparent,
  ButtonSecondary,
  DoubleContent,
  Headline,
  InputControl,
  Link,
  Loading,
  ModalError,
  ReCaptchaControl,
  SectionContent,
  Text,
  ZlCodeBarIcon,
} from "../../../../components";
import { chatIdConsorcio, links } from "../../../../config";

import { SegundaViaDeBoletoForm } from "../../../../forms";
import { useBoletosConsorcios } from "../../../../hooks";
import { applyKey } from "../../../../utils";
import * as S from "./SegundaViaDeBoleto.Style";
import { ModalContext } from "../../../../contexts";

const BoletoDetail = (props) => {
  const { label, value, children } = props;
  return (
    <S.DetailItem title={label}>
      <S.DetailItemLabel
        as={Text}
        elementType="legend"
        text={label}
        textType="caption"
      />
      <S.DetailItemValue as={Text} elementType="div" textSize="sm">
        {value || children}
      </S.DetailItemValue>
    </S.DetailItem>
  );
};

BoletoDetail.defaultProps = {
  value: null,
};

BoletoDetail.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
};

const GerarBoletoAction = ({ onClick }) => {
  const onClickHandle = useCallback(
    (evt) => {
      evt.preventDefault();
      if (onClick) {
        onClick(evt);
      }
    },
    [onClick]
  );

  return (
    <S.BoletoAction href="#" as={Action} onClick={onClickHandle}>
      <S.BoletoActionIcon as={ZlCodeBarIcon} />
      <S.BoletoActionLabel
        as={Text}
        textType="button"
        reset={{ marginBottom: "0px" }}
        text="Gerar boleto"
        elementType="span"
      />
    </S.BoletoAction>
  );
};

GerarBoletoAction.defaultProps = { onClick: null };
GerarBoletoAction.propTypes = { onClick: PropTypes.func };

const ResetFormButton = (props) => {
  const { resetForm } = useFormikContext();

  return (
    <S.BoletoAction
      {...props}
      elementType="button"
      as={Action}
      onClick={resetForm}
    >
      <S.BoletoActionIcon as={BiRefresh} />
      <S.BoletoActionLabel
        as={Text}
        textType="button"
        reset={{ marginBottom: "0px" }}
        text="Nova Consulta"
        elementType="span"
      />
    </S.BoletoAction>
  );
};

const CabecalhoDesktop = (props) => {
  return (
    <S.BoletoDetails {...props}>
      <S.BoletoDailsContent>
        <BoletoDetail label="Vencimento" />
        <BoletoDetail label="Valor da parcela" />
        <BoletoDetail label="Multa/juros" />
        <BoletoDetail label="Valor a pagar" />
      </S.BoletoDailsContent>
      <S.BoletoActionContent />
    </S.BoletoDetails>
  );
};

const BoletoDetails = ({
  dthVencimento,
  vlrDevido,
  vlrMulta,
  vlrParcela,
  onSelected,
}) => {
  return (
    <S.BoletoDetails>
      <S.BoletoDailsContent>
        <BoletoDetail label="Vencimento" value={dthVencimento} />
        <BoletoDetail label="Valor da parcela" value={vlrParcela} />
        <BoletoDetail label="Multa/juros" value={vlrMulta} />
        <BoletoDetail label="Valor a pagar" value={vlrDevido} />
      </S.BoletoDailsContent>
      <S.BoletoActionContent>
        <GerarBoletoAction onClick={onSelected} />
      </S.BoletoActionContent>
    </S.BoletoDetails>
  );
};

BoletoDetails.defaultProps = {};
BoletoDetails.propTypes = {
  dthVencimento: PropTypes.string.isRequired,
  vlrDevido: PropTypes.string.isRequired,
  vlrMulta: PropTypes.string.isRequired,
  vlrParcela: PropTypes.string.isRequired,
  onSelected: PropTypes.func.isRequired,
};

const BoletosEmAberto = ({ boletos, onBoletoSelected }) => {
  const boletosValidos = useMemo(
    () => boletos.filter(({ idBoleto }) => idBoleto),
    [boletos]
  );

  return (
    <>
      {boletosValidos.length ? (
        <>
          <Text textSize="lg">
            <strong>Importante: </strong>Após gerar o boleto,{" "}
            <strong>
              a linha digitável deve começar com o código da Caixa 10496
            </strong>
            . Caso contrário, não efetue o pagamento e{" "}
            <ActionChat
              chatId={chatIdConsorcio}
              as={Link}
              label="fale com a gente pelo Chat"
            />
            . Se preferir, ligue para a nossa central de relacionamento:{" "}
            <Link href="tel:0800 274 1000" label="0800 274 1000" />, das 8h às
            21h, de segunda a sexta, exceto em feriados nacionais.
          </Text>
          <S.DisplayQuantidade as={Text} textSize="sm">
            Essa cota possui{" "}
            <strong>{boletos.length} parcelas em aberto</strong>.
          </S.DisplayQuantidade>
          <S.BoletosAberto>
            <S.CabecalhoDesktop as={CabecalhoDesktop} />
            {applyKey(boletos, (boleto) => (
              <BoletoDetails
                {...boleto}
                onSelected={() => onBoletoSelected({ ...boleto })}
              />
            ))}
          </S.BoletosAberto>
        </>
      ) : (
        <>
          <Text textSize="lg" bodyStrong>
            Até o momento, essa cota não possui parcelas em aberto.
          </Text>
          <Text textSize="lg">
            Lembrete: para receber seus boletos mensalmente por e-mail, mantenha
            seu cadastro sempre atualizado acessando a{" "}
            <Link
              href={links.consorcio.areaLogada}
              target="_blank"
              label="Área do Cliente"
            />
            .
          </Text>
        </>
      )}
      <S.ResetForm as={ResetFormButton} />
    </>
  );
};

BoletosEmAberto.defaultProps = { boletos: [] };
BoletosEmAberto.propTypes = {
  boletos: PropTypes.arrayOf(
    PropTypes.shape({
      dthVencimento: PropTypes.string.isRequired,
      vlrDevido: PropTypes.string.isRequired,
      vlrMulta: PropTypes.string.isRequired,
      vlrParcela: PropTypes.string.isRequired,
    })
  ),
  onBoletoSelected: PropTypes.func.isRequired,
};

const DescriptionContent = ({ showResults }) => {
  const {
    values: { grupo, cota, nomeConsorcio },
  } = useFormikContext();

  return (
    <>
      <Headline nivel="2" styleAs="5">
        2º via de boleto
      </Headline>

      {showResults ? (
        <>
          {nomeConsorcio && (
            <Text
              reset={{ marginBottom: "4px" }}
              text={`Consórcio ${nomeConsorcio}`}
              textSize="sm"
            />
          )}
          <S.DescriptionConsorcio as={Text} textType="caption">
            Grupo {grupo.padStart(6, "0")} | Cota {cota.padStart(6, "0")}
          </S.DescriptionConsorcio>
        </>
      ) : (
        <>
          <Text textSize="lg">
            Preencha os campos para visualizar o boleto.
          </Text>
          <Text textSize="lg">
            <strong>Importante lembrar:</strong> para receber seus boletos
            mensalmente por e-mail, mantenha seu cadastro sempre atualizado
            acessando a{" "}
            <Link
              href={links.consorcio.areaLogada}
              target="_blank"
              label="Área do Cliente"
            />
            .
          </Text>
        </>
      )}
    </>
  );
};

DescriptionContent.defaultProps = { showResults: null };

DescriptionContent.propTypes = {
  showResults: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.instanceOf(Object),
    PropTypes.element,
  ]),
};

const FormularioSegundaViaBoleto = ({ results, downloadBoletoPdf }) => {
  const {
    dirty,
    isValid,
    submitForm,
    setFieldValue,
    values: { results: dataResults },
  } = useFormikContext();

  const showResults = useMemo(() => dataResults?.boletos, [
    dataResults?.boletos,
  ]);

  useEffect(() => {
    setFieldValue("results", results);
    return () => {
      setFieldValue("results", null);
    };
  }, [results, setFieldValue]);

  return (
    <>
      <DoubleContent
        collumnSize={showResults ? 4 : 7.5}
        leftContent={<DescriptionContent showResults={showResults} />}
        rightContent={
          !showResults ? (
            <>
              <InputControl
                label="CPF ou CNPJ"
                name="cpfCnpj"
                placeholder="Apenas números"
                mask="cpf/cnpj"
              />
              <S.GrupoCotaContent>
                <S.InputGrupoCota
                  label="Grupo"
                  name="grupo"
                  placeholder="Apenas números"
                />
                <S.InputGrupoCota
                  label="Cota"
                  name="cota"
                  placeholder="Apenas números"
                />
              </S.GrupoCotaContent>
              <ReCaptchaControl />
              <S.ButtonForm
                as={ButtonSecondary}
                variant="secondary"
                disabled={!dirty || !isValid}
                onClick={submitForm}
              >
                Visualizar boleto
              </S.ButtonForm>
            </>
          ) : (
            <BoletosEmAberto
              boletos={dataResults?.boletos}
              onBoletoSelected={downloadBoletoPdf}
            />
          )
        }
      />
    </>
  );
};

FormularioSegundaViaBoleto.defaultProps = {
  results: null,
};
FormularioSegundaViaBoleto.propTypes = {
  results: PropTypes.objectOf(
    PropTypes.shape({
      boletos: PropTypes.arrayOf(
        PropTypes.shape({
          dthVencimento: PropTypes.string.isRequired,
          vlrDevido: PropTypes.string.isRequired,
          vlrMulta: PropTypes.string.isRequired,
          vlrParcela: PropTypes.string.isRequired,
        })
      ),
    })
  ),
  downloadBoletoPdf: PropTypes.func.isRequired,
};

const ModalErrorBody = ({ onTryAgain }) => {
  const { closeHandle } = useContext(ModalContext);

  const tryAgainHandle = useCallback(
    (evt) => {
      if (onTryAgain) {
        onTryAgain();
      }
      closeHandle(evt);
    },
    [closeHandle, onTryAgain]
  );

  return (
    <>
      <Text>
        Estamos com algum problema em nossos sistemas e não conseguimos concluir
        sua solicitação. Por favor, tente de novo mais tarde.
      </Text>
      <S.ErrorModalButton
        as={ButtonPrimary}
        label="Tentar de novo"
        onClick={tryAgainHandle}
      />
      <S.CustomCloseModal
        as={ButtonPrimaryTransparent}
        label="Fechar"
        onClick={closeHandle}
      />
    </>
  );
};

ModalErrorBody.defaultProps = {
  onTryAgain: null,
};

ModalErrorBody.propTypes = {
  onTryAgain: PropTypes.func,
};

const SegundaViaDeBoleto = () => {
  const {
    boletosEmAberto,
    loading,
    listarBoletosEmAtraso,
    recuperarBoletoPdf,
    requestAgain,
    error,
  } = useBoletosConsorcios();

  const buscarBoletosHandle = useCallback(
    (dadosBusca) => {
      listarBoletosEmAtraso({ ...dadosBusca });
    },
    [listarBoletosEmAtraso]
  );

  const downloadBoletoPdfHandle = useCallback(
    (boleto) => {
      recuperarBoletoPdf({ ...boleto });
    },
    [recuperarBoletoPdf]
  );

  return (
    <Loading loading={loading}>
      <SectionContent>
        <SegundaViaDeBoletoForm
          form={FormularioSegundaViaBoleto}
          onSubmit={buscarBoletosHandle}
          downloadBoletoPdf={downloadBoletoPdfHandle}
          results={boletosEmAberto}
        />
      </SectionContent>
      <S.CustomModalError as={ModalError} error={!loading && error}>
        <ModalErrorBody onTryAgain={requestAgain} />
      </S.CustomModalError>
    </Loading>
  );
};

export default SegundaViaDeBoleto;
